// src/pages/admin/AdminSidebar.tsx
import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { all_routes } from "../router/all_routes";
import { useAuth } from "../auth/AuthProvider";
import ImageWithBasePath from "../../core/common/imageWithBasePath";
import axios from "axios";

type AdminInfo = {
    id: number;
    nome: string;
    email: string;
    role: "SUPER" | "COMUM";
};

const AdminSidebar = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { user, setUser, setIsLoggedIn } = useAuth();
    const BASE_URL = process.env.REACT_APP_API_URL;
    const token = localStorage.getItem("token");

    const [admin, setAdmin] = useState<AdminInfo | null>(null);
    const [unreadTickets, setUnreadTickets] = useState<number>(0); // <- total de tickets não lidos

    // Buscar informações do admin
    useEffect(() => {
        if (!token) return;

        axios
            .get(`${BASE_URL}/admins/role`, {
                headers: { Authorization: `Bearer ${token}` },
            })
            .then((res) => {
                const role = res.data === "SUPER_ADMIN" ? "SUPER" : "COMUM";
                setAdmin({
                    id: 0,
                    nome: user?.nome || "",
                    email: user?.email || "",
                    role,
                });
            })
            .catch(() => {
                localStorage.removeItem("token");
                setUser(null);
                setIsLoggedIn(false);
                navigate("/");
            });
    }, [token, user, setUser, setIsLoggedIn, navigate, BASE_URL]);

    // Buscar total de tickets não lidos
    useEffect(() => {
        if (!token) return;

        const fetchUnreadTickets = async () => {
            try {
                const res = await axios.get(`${BASE_URL}/admins/tickets/unread-count`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setUnreadTickets(res.data?.count || 0);
            } catch (err) {
                console.error("Erro ao buscar tickets não lidos:", err);
            }
        };

        fetchUnreadTickets();
    }, [token, BASE_URL]);

    const handleLogout = (e: React.MouseEvent) => {
        e.preventDefault();
        localStorage.removeItem("token");
        setUser(null);
        setIsLoggedIn(false);
        navigate("/");
    };

    return (
        <div className="card user-sidebar mb-4 mb-lg-0 theiaStickySidebar">
            <div className="card-header user-sidebar-header">
                <div className="profile-content rounded-pill">
                    <div className="d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center">
                            {user?.foto?.url ? (
                                <img
                                    src={`${BASE_URL}/uploads/${user.foto.id}`}
                                    alt="Foto de perfil"
                                    className="img-fluid avatar avatar-lg rounded-circle flex-shrink-0 me-1"
                                    style={{ objectFit: "cover", width: "60px", height: "60px" }}
                                />
                            ) : (
                                <ImageWithBasePath
                                    src="assets/img/users/user-01.jpg"
                                    alt="image"
                                    className="img-fluid avatar avatar-lg rounded-circle me-2"
                                />
                            )}
                            <div>
                                <h6 className="fs-16 mb-0">
                                    {user?.nome || user?.email || "Administrador"}
                                </h6>
                                <small className="text-muted">
                                    {admin?.role === "SUPER" ? "Super Admin" : "Admin Comum"}
                                </small>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="card-body user-sidebar-body">
                <ul>
                    <li>
                        <span className="fs-14 text-gray-3 fw-medium mb-2">
                            Administração
                        </span>
                    </li>

                    <li>
                        <Link
                            to={all_routes.adminPanel}
                            className={`d-flex align-items-center ${location.pathname === all_routes.adminPanel ? "active" : ""}`}
                        >
                            <i className="isax isax-dashboard-2" />
                            Painel
                        </Link>
                    </li>

                    {admin?.role === "SUPER" && (
                        <li>
                            <Link
                                to={all_routes.adminUsers}
                                className={`d-flex align-items-center ${location.pathname.includes("/admin/admin-users") ? "active" : ""}`}
                            >
                                <i className="isax isax-user" />
                                Gerenciar Admins
                            </Link>
                        </li>
                    )}

                    <li>
                        <Link
                            to={all_routes.adminEmpresas}
                            className={`d-flex align-items-center ${location.pathname.includes("/admin/empresas") ? "active" : ""}`}
                        >
                            <i className="isax isax-building" />
                            Empresas
                        </Link>
                    </li>

                    <li>
                        <Link
                            to={all_routes.adminVeiculos}
                            className={`d-flex align-items-center ${location.pathname.includes("/admin/admin-veiculos") ? "active" : ""}`}
                        >
                            <i className="isax isax-car" />
                            Veículos
                        </Link>
                    </li>

                    {admin?.role === "SUPER" && (
                        <li>
                            <Link
                                to={all_routes.adminComissaoEmpresa}
                                className={`d-flex align-items-center ${location.pathname.includes("/admin/comissao") ? "active" : ""}`}
                            >
                                <i className="isax isax-percentage-square" />
                                Comissão das empresas
                            </Link>
                        </li>
                    )}

                    <li>
                        <Link
                            to={all_routes.servicesAdmin}
                            className={`d-flex align-items-center ${location.pathname.includes("/admin/services") ? "active" : ""}`}
                        >
                            <i className="isax isax-briefcase" />
                            Serviços
                        </Link>
                    </li>

                    {/* Pacotes */}
                    <li>
                        <Link
                            to="/admin/pacotes"
                            className={`d-flex align-items-center ${location.pathname.includes("/admin/pacotes") ? "active" : ""}`}
                        >
                            <i className="isax isax-box" />
                            Pacotes
                        </Link>
                    </li>

                    {/* Compras de Pacotes */}
                    <li>
                        <Link
                            to={all_routes.adminPacotesCompras}
                            className={`d-flex align-items-center ${location.pathname.includes("/admin/pacote-compras") ? "active" : ""}`}
                        >
                            <i className="isax isax-bag" />
                            Compras de Pacotes
                        </Link>
                    </li>

                    {/* Avaliações */}
                    <li>
                        <Link
                            to={all_routes.adminAvaliacoes}
                            className={`d-flex align-items-center ${location.pathname.includes("/admin/avaliacoes") ? "active" : ""}`}
                        >
                            <i className="isax isax-star" /> {/* você pode trocar o ícone se quiser */}
                            Avaliações
                        </Link>
                    </li>

                    {/* Postagens */}
                    <li>
                        <Link
                            to={all_routes.adminPostagens}
                            className={`d-flex align-items-center ${location.pathname.includes("/admin/postagens") ? "active" : ""
                                }`}
                        >
                            <i className="isax isax-gallery" /> {/* Escolhi um ícone de galeria/fotos, mas você pode trocar */}
                            Postagens
                        </Link>
                    </li>

                    <li>
                        <Link
                            to={all_routes.adminTickets}
                            className={`d-flex align-items-center ${location.pathname.includes("/admin/tickets") ? "active" : ""}`}
                        >
                            <i className="isax isax-ticket" />
                            Tickets
                            {unreadTickets > 0 && (
                                <span className="badge bg-danger ms-auto">{unreadTickets}</span>
                            )}
                        </Link>
                    </li>

                    <li>
                        <Link
                            to="/"
                            onClick={handleLogout}
                            className="d-flex align-items-center"
                        >
                            <i className="isax isax-logout" /> Sair
                        </Link>
                    </li>
                </ul>
            </div>
        </div>
    );
};

export default AdminSidebar;
